import { useRef, useEffect, useState, useCallback } from 'react';

export interface TimeSeriesPoint {
  x: number; // timestamp
  y: number;
}

export function useVisxZoomDomainTimeSeries({
  initialXDomain,
  initialYDomain,
  points,
  innerWidth,
}: {
  initialXDomain: [number, number];
  initialYDomain: [number, number];
  points: TimeSeriesPoint[];
  innerWidth: number;
}) {
  const [xDomain, setXDomain] = useState<[number, number]>(initialXDomain);
  const [yDomain, setYDomain] = useState<[number, number]>(initialYDomain);
  const zoomRef = useRef<any>(null);
  const rafRef = useRef<number | null>(null);
  const latestTransformRef = useRef<any>(null);
  const latestPointsRef = useRef(points);
  const latestInitialXDomainRef = useRef(initialXDomain);
  const latestInitialYDomainRef = useRef(initialYDomain);
  const latestInnerWidthRef = useRef(innerWidth);

  useEffect(() => {
    latestPointsRef.current = points;
    latestInitialXDomainRef.current = initialXDomain;
    latestInitialYDomainRef.current = initialYDomain;
    latestInnerWidthRef.current = innerWidth;
  }, [points, initialXDomain, initialYDomain, innerWidth]);

  const scheduleDomainUpdate = useCallback((transform: any, zoom?: any) => {
    latestTransformRef.current = transform;
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      const points = latestPointsRef.current;
      const initialXDomain = latestInitialXDomainRef.current;
      const initialYDomain = latestInitialYDomainRef.current;
      const innerWidth = latestInnerWidthRef.current;
      const xRange = initialXDomain[1] - initialXDomain[0];
      // Calculate allowed translateX range
      const minTranslateX = 0;
      const maxTranslateX = innerWidth * (1 - transform.scaleX);
      let clampedTranslateX = transform.translateX;
      if (clampedTranslateX > minTranslateX) clampedTranslateX = minTranslateX;
      if (clampedTranslateX < maxTranslateX) clampedTranslateX = maxTranslateX;
      if (clampedTranslateX !== transform.translateX) {
        // Update the zoom transform to the clamped value
        transform = { ...transform, translateX: clampedTranslateX };
        if (zoom && typeof zoom.setTransformMatrix === 'function') {
          zoom.setTransformMatrix(transform);
        }
      }
      // Now recalculate newXDomain using the clamped transform
      let newXDomain: [number, number] = [
        initialXDomain[0] - (clampedTranslateX / innerWidth) * xRange,
        initialXDomain[0] + (((innerWidth - clampedTranslateX) / innerWidth) * xRange) / transform.scaleX,
      ];
      if (newXDomain[0] > newXDomain[1]) newXDomain = [newXDomain[1], newXDomain[0]];
      // Find all visible points in the new x domain
      const visiblePoints = points.filter((d) => d.x >= newXDomain[0] && d.x <= newXDomain[1]);
      let yMin = Math.min(...visiblePoints.map((d) => d.y));
      let yMax = Math.max(...visiblePoints.map((d) => d.y));
      if (!isFinite(yMin) || !isFinite(yMax) || visiblePoints.length < 3) {
        yMin = initialYDomain[0];
        yMax = initialYDomain[1];
      }
      // Add a small margin
      const margin = (yMax - yMin) * 0.01;
      const newYDomain: [number, number] = [yMin - margin, yMax + margin];
      setXDomain(newXDomain);
      setYDomain(newYDomain);
      rafRef.current = null;

      // If fully zoomed out, use initial yMax
      if (newXDomain[0] === initialXDomain[0] && newXDomain[1] === initialXDomain[1]) {
        setYDomain(initialYDomain);
      }

      // Prevent panning beyond initial xDomain when scale is 1
      if (transform.scaleX === 1) {
        newXDomain = [initialXDomain[0], initialXDomain[1]];
      } else {
        // Clamp newXDomain to initial domain extents
        newXDomain[0] = Math.max(newXDomain[0], initialXDomain[0]);
        newXDomain[1] = Math.min(newXDomain[1], initialXDomain[1]);
        if (newXDomain[0] > newXDomain[1]) newXDomain = [initialXDomain[0], initialXDomain[1]];
      }
    });
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      if (zoomRef.current) {
        scheduleDomainUpdate(zoomRef.current.transformMatrix, zoomRef.current);
      }
    }, 16); // ~60fps
    return () => clearInterval(id);
  }, [scheduleDomainUpdate]);

  return { xDomain, yDomain, zoomRef };
}
